import type { TeacherListItem, TimetableBlock } from '@classgo/shared';

import type { SchoolYearView } from '../../academic/types';
import type { Slot, Weekday } from './types';

/**
 * The weekdays the API accepts, in order.
 */
export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6];

/**
 * The default slot of a new block: Monday at seven, one hour long.
 */
export const DEFAULT_SLOT: Slot = { weekday: 1, startsAt: '07:00', endsAt: '08:00' };

/**
 * Minutes since midnight of an `HH:MM` clock.
 *
 * @param {string} clock -- The time.
 * @returns The minutes.
 */
export const minutesOf = (clock: string): number => {
  const [hours = 0, minutes = 0] = clock.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * The `HH:MM` clock `minutes` after `clock`, kept inside the day.
 *
 * @param {string} clock -- The start.
 * @param {number} minutes -- How much later.
 * @returns The time.
 */
export const addMinutes = (clock: string, minutes: number): string => {
  const total = Math.min(23 * 60 + 59, Math.max(0, minutesOf(clock) + minutes));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * A block's length in minutes.
 *
 * @param {object} block -- Its hours.
 * @returns The minutes.
 */
export const durationOf = (block: { startsAt: string; endsAt: string }): number =>
  minutesOf(block.endsAt) - minutesOf(block.startsAt);

/**
 * Narrows a block's weekday to the grid's union (the API only stores 1 to 6).
 *
 * @param {number} value -- The stored weekday.
 * @returns The weekday, Monday for anything unexpected.
 */
export const weekdayOf = (value: number): Weekday => WEEKDAYS.find((day) => day === value) ?? 1;

/**
 * The weekdays a grid shows: Monday to Friday, plus Saturday when a block sits there.
 *
 * @param {TimetableBlock[]} blocks -- The week.
 * @returns The columns.
 */
export const weekdaysOf = (blocks: readonly TimetableBlock[]): Weekday[] =>
  WEEKDAYS.filter((day) => day <= 5 || blocks.some((block) => block.weekday === 6));

/**
 * Distinct start times of the blocks, in clock order: the rows of the grid.
 *
 * @param {TimetableBlock[]} blocks -- The week.
 * @returns The rows.
 */
export const startTimesOf = (blocks: readonly TimetableBlock[]): string[] =>
  [...new Set(blocks.map((block) => block.startsAt))].sort();

/**
 * A teacher's display name, or the email when the profile has no name yet.
 *
 * @param {TeacherListItem} teacher -- The teacher.
 * @returns The name.
 */
export const teacherName = (teacher: TeacherListItem): string =>
  [teacher.givenName, teacher.familyName].filter(Boolean).join(' ') || teacher.email || teacher.id;

/**
 * "2027" for the active year and "2027 (Planeado)" otherwise, with the status already translated.
 *
 * @param {SchoolYearView} year -- The year.
 * @param {string} status -- Its translated status.
 * @returns The label.
 */
export const yearLabel = (year: SchoolYearView, status: string): string =>
  year.status === 'active' ? year.name : `${year.name} (${status})`;
